import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { auth, db, ref, get, set } from "../Firebase/firebase-config";

const AccountContext = createContext();

export const AccountProvider = ({ children }) => {
  const [currentAccount, setCurrentAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountSwitchTrigger, setAccountSwitchTrigger] = useState(0);

  // Load all accounts for main + firms
  const loadAllAccounts = async (main) => {
    const firms = [];

    if (main.type === "main") {
      const firmsSnap = await get(ref(db, `profileDashboard/${main.uid}/firms`));
      if (firmsSnap.exists()) {
        const allFirms = firmsSnap.val();
        for (const firmUid in allFirms) {
          const firmDataSnap = await get(ref(db, `profileDashboard/${firmUid}`));
          if (firmDataSnap.exists()) {
            const acc = firmDataSnap.val();
            firms.push({
              uid: firmUid,
              name: acc.company_name || "Unnamed Firm",
              type: "firm",
              mainUid: main.uid,
              parentUid: main.uid,
              company_name: acc.company_name,
              ...acc,
            });
          }
        }
      }
    }

    const mainAcc = {
      uid: main.uid,
      name: main.company_name || "My Account",
      type: main.type || "main",
      mainUid: main.uid,
      company_name: main.company_name,
      ...main,
    };

    const allAccounts = [mainAcc, ...firms];
    setAccounts(allAccounts);

    // Get last selected account from DB
    const selectedSnap = await get(ref(db, `profileDashboard/${main.uid}/currentAccountUid`));
    const selectedUid = selectedSnap.exists() ? selectedSnap.val() : main.uid;
    const selected = allAccounts.find(a => a.uid === selectedUid) || mainAcc;
    setCurrentAccount(selected);

    setLoading(false);
  };

  // Auth listener
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setCurrentAccount(null);
        setAccounts([]);
        setLoading(false);
        return;
      }

      const mainSnap = await get(ref(db, `profileDashboard/${user.uid}`));
      if (!mainSnap.exists()) {
        setLoading(false);
        return;
      }

      const main = { uid: user.uid, ...mainSnap.val() };
      await loadAllAccounts(main);
    });

    return unsub;
  }, []);

  // Switch account and save in DB
  const switchAccount = useCallback(async (uid) => {
    const acct = accounts.find((a) => a.uid === uid);
    if (acct) {
      setCurrentAccount(acct);
      if (auth.currentUser) {
        await set(ref(db, `profileDashboard/${auth.currentUser.uid}/currentAccountUid`), uid);
      }
      setAccountSwitchTrigger(prev => prev + 1);
    }
  }, [accounts]);

  const reload = async () => {
    if (auth.currentUser) {
      const mainSnap = await get(ref(db, `profileDashboard/${auth.currentUser.uid}`));
      if (mainSnap.exists()) {
        const main = { uid: auth.currentUser.uid, ...mainSnap.val() };
        await loadAllAccounts(main);
      }
    }
  };

  return (
    <AccountContext.Provider value={{
      currentAccount,
      accounts,
      switchAccount,
      loading,
      reload,
      accountSwitchTrigger
    }}>
      {children}
    </AccountContext.Provider>
  );
};

export const useAccount = () => useContext(AccountContext);
